import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getValidationErrorInstrumenWithStartDate } from 'v2/modules/withRunForm/components/RunFormHeader/HistoricDateRangePicker/error';
import { Pipe } from 'v3/quantConfig/types/pipe.type';
import { Case } from 'v3/quantConfig/types/quantConfig.type';
import { InitialState } from 'v3/common/components/DatePicker/DateRange/type';
import { formattedDateRange } from './utility';

const UNIQUE_ID = 'date-range';

type DateValidationProps = {
  isHistoric: boolean;
  instrumentGroups: string[][];
  cases: Case[];
  pipeConfigs: Pipe[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setError: (id: string, error: any) => void;
  clearErrors: (id: string) => void;
  reduxStart: Date | null;
  reduxEnd: Date | null;
};

export const useDateRange = ({
  isHistoric,
  instrumentGroups,
  cases,
  pipeConfigs,
  setError,
  clearErrors,
  reduxStart,
  reduxEnd,
}: DateValidationProps) => {
  const [initialState, setInitialState] = useState<InitialState>({
    isShowCustom: false,
    labelIndex: null,
  });
  const { isShowCustom, labelIndex } = initialState;
  const startDate = useMemo(() => (reduxStart ? new Date(reduxStart) : null), [reduxStart]);
  const endDate = useMemo(() => (reduxEnd ? new Date(reduxEnd) : null), [reduxEnd]);

  const { startRange, endRange, dayDifference } = useMemo(
    () => formattedDateRange({ startDate, endDate }),
    [startDate, endDate]
  );
  const onCloseRightContent = useCallback(() => {
    setInitialState((prev) => ({
      ...prev,
      isShowCustom: false,
      labelIndex: prev.labelIndex,
    }));
  }, []);

  useEffect(() => {
    if (!isHistoric) {
      clearErrors(UNIQUE_ID);
      return;
    }
    const validStartDate = moment(reduxStart).toDate();
    const { isValid, message } = getValidationErrorInstrumenWithStartDate(
      instrumentGroups,
      validStartDate,
      cases,
      pipeConfigs
    );

    if (isValid) {
      clearErrors(UNIQUE_ID);
    } else {
      setError(UNIQUE_ID, { message });
      reactToastify(message, TOASTIFY_TYPES.WARNING);
    }
  }, [isHistoric, instrumentGroups, cases, pipeConfigs, setError, clearErrors, reduxStart]);

  return {
    isShowCustom,
    labelIndex,
    startDate,
    endDate,
    startRange,
    endRange,
    dayDifference,
    UNIQUE_ID,
    setIsShowCustom: (value: boolean) => {
      setInitialState((prev) => ({ ...prev, isShowCustom: value }));
    },
    onCloseRightContent,
    setLabelIndex: (index: number | null) => {
      setInitialState((prev) => ({ ...prev, labelIndex: index }));
    },
  };
};
